import React from 'react';
import { CheckCircle, Circle, ArrowRight, Sparkle } from '@phosphor-icons/react';
import { Button } from '../ui/Button';
import { PAGE_ROUTE } from '../../lib/routes';
import { loadOnboardingDraft } from '../../lib/utils';
interface OnboardingChecklistProps {
  navigate: (page: string) => void;
  userName?: string;
}
export function OnboardingChecklist({
  navigate,
  userName
}: OnboardingChecklistProps) {
  const firstName = userName?.split(' ')[0] || 'Bun venit';
  // Detect resume state (draft started but not published)
  const draft = loadOnboardingDraft();
  const draftSubStep = draft?.stage === 'anunt' ? draft.subStep ?? 0 : 0;
  const hasDraft = draftSubStep > 0;
  const draftStepPercent = hasDraft ? Math.round(draftSubStep / 5 * 100) : 0;
  const ctaLabel = hasDraft ? 'Continuă anunțul' : 'Creează-ți anunțul';
  const heroSub = hasDraft ? `Anunțul tău e ${draftStepPercent}% completat. Continuă unde ai rămas.` : 'Mai ai un pas: creează-ți anunțul ca să apari în fața proprietarilor verificați.';
  const anuntDescription = hasDraft ? `${draftStepPercent}% completat — continuă unde ai rămas` : 'Spune-ne ce cauți (durează ~2 minute)';
  // Combined progress: 2/3 for cont+profil, plus partial credit for in-progress anunț
  const totalProgress = hasDraft ? Math.min(99, Math.round(66 + draftStepPercent * 0.34)) : 66;
  const totalLabel = hasDraft ? `${totalProgress}% completat` : '2 din 3 · 66%';
  return <div className="rounded-2xl bg-dark-2 border border-dark-3 overflow-hidden" data-id="element-682">
      {/* Hero */}
      <div className="bg-lime-5 border-b border-lime-4 px-6 sm:px-8 py-6" data-id="element-683">
        <div className="flex items-center gap-2 mb-2" data-id="element-684">
          <Sparkle weight="fill" className="text-lime-1" size={18} data-id="element-685" />
          <span className="text-xs font-semibold uppercase tracking-wider text-lime-1" data-id="element-686">
            {hasDraft ? 'În progres' : 'Aproape gata'}
          </span>
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-light-1" data-id="element-687">
          {hasDraft ? `Hai să terminăm, ${firstName}` : `Bun venit, ${firstName}!`}
        </h2>
        <p className="text-sm text-light-2 mt-1.5 leading-relaxed" data-id="element-688">{heroSub}</p>
      </div>

      {/* Body */}
      <div className="px-6 sm:px-8 py-6" data-id="element-689">
        <ul className="space-y-3 mb-6" data-id="element-690">
          <ChecklistItem checked label="Cont creat" data-id="element-691" />
          <ChecklistItem checked label="Profil completat" data-id="element-692" />
          <ChecklistItem checked={false} label="Anunț activ" description={anuntDescription} highlighted data-id="element-693" />
        </ul>

        {/* Progress */}
        <div className="mb-6" data-id="element-694">
          <div className="flex items-center justify-between mb-2" data-id="element-695">
            <span className="text-xs font-medium text-light-3" data-id="element-696">Progres</span>
            <span className="text-xs font-medium text-lime-1" data-id="element-697">
              {totalLabel}
            </span>
          </div>
          <div className="h-1.5 bg-dark-4 rounded-full overflow-hidden" data-id="element-698">
            <div className="h-full bg-lime-1 rounded-full transition-all duration-500" style={{
            width: `${totalProgress}%`
          }} data-id="element-699" />
          </div>
        </div>

        <Button onClick={() => navigate(PAGE_ROUTE.ONBOARDING)} className="w-full h-12 bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold" data-id="element-700">
          <span className="flex items-center justify-center gap-2" data-id="element-701">
            {ctaLabel}
            <ArrowRight size={18} weight="bold" data-id="element-702" />
          </span>
        </Button>

        <p className="text-xs text-light-3 text-center mt-4 leading-relaxed" data-id="element-703">
          Anunțul tău va fi vizibil în Marketplace pentru proprietari
          verificați. Tu primești ofertele, tu alegi cu cine vorbești.
        </p>
      </div>
    </div>;
}
interface ChecklistItemProps {
  checked: boolean;
  label: string;
  description?: string;
  highlighted?: boolean;
}
function ChecklistItem({
  checked,
  label,
  description,
  highlighted
}: ChecklistItemProps) {
  return <li className="flex items-start gap-3" data-id="element-704">
      <div className="shrink-0 mt-0.5" data-id="element-705">
        {checked ? <CheckCircle weight="fill" size={22} className="text-lime-1" data-id="element-706" /> : <Circle weight="bold" size={22} className={highlighted ? 'text-lime-1' : 'text-light-4'} data-id="element-707" />}
      </div>
      <div className="flex-1 min-w-0" data-id="element-708">
        <p className={`text-sm font-medium ${checked ? 'text-light-3 line-through decoration-light-4' : 'text-light-1'}`} data-id="element-709">
          {label}
        </p>
        {description && <p className="text-xs text-light-3 mt-0.5" data-id="element-710">{description}</p>}
      </div>
    </li>;
}